import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * §v3 — small status/sentiment/AI-marker chip.
 *
 * Tones use a 22% fill + 45% border of the same color, on top of glass.
 * The kit is explicit: never use raw chrome colors here, use semantic
 * vars so the chip degrades cleanly if a tenant overrides accents.
 *
 * Tone palette:
 *   neutral — glass-2 + hairline. Generic "chip" with no semantic load.
 *   ai      — accent-sky tinted. The ✦ AI marker; reserved for AI-
 *             suggested copy + agent meta.
 *   ok      — success. "Paid", "Delivered", "Confirmed".
 *   warn    — warning. "Awaiting pickup", "Pending".
 *   bad     — danger. "Refunded", "Cancelled", "Bounced".
 *   info    — info. "Active" sessions, "In progress".
 *
 * Sentiment is its own helper because it uses the luminance ramp,
 * not the semantic palette.
 */

export type ChipTone =
  | "neutral"
  | "ai"
  | "ok"
  | "warn"
  | "bad"
  | "info"
  | "sentiment-pos"
  | "sentiment-neu"
  | "sentiment-neg";

const TONE_STYLE: Record<ChipTone, React.CSSProperties> = {
  neutral: {
    background: "var(--glass-2)",
    color: "var(--fg-1)",
    borderColor: "var(--glass-hairline-strong)",
  },
  ai: {
    background: "color-mix(in oklab, var(--accent-sky) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--accent-sky) 45%, transparent)",
  },
  ok: {
    background: "color-mix(in oklab, var(--success) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--success) 45%, transparent)",
  },
  warn: {
    background: "color-mix(in oklab, var(--warning) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--warning) 45%, transparent)",
  },
  bad: {
    background: "color-mix(in oklab, var(--danger) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--danger) 45%, transparent)",
  },
  info: {
    background: "color-mix(in oklab, var(--info) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--info) 45%, transparent)",
  },
  "sentiment-pos": {
    background: "color-mix(in oklab, var(--sentiment-pos) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--sentiment-pos) 45%, transparent)",
  },
  "sentiment-neu": {
    background: "color-mix(in oklab, var(--sentiment-neu) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--sentiment-neu) 45%, transparent)",
  },
  "sentiment-neg": {
    background: "color-mix(in oklab, var(--sentiment-neg) 22%, transparent)",
    color: "var(--fg-1)",
    borderColor: "color-mix(in oklab, var(--sentiment-neg) 45%, transparent)",
  },
};

export interface GlassChipProps {
  tone?: ChipTone;
  /** Optional dot before the label — small colored bullet for status. */
  dot?: boolean;
  /** Hide on screens narrower than `sm` — useful in dense rows. */
  hideOnMobile?: boolean;
  className?: string;
  children: ReactNode;
}

export function GlassChip({
  tone = "neutral",
  dot = false,
  hideOnMobile = false,
  className,
  children,
}: GlassChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5",
        "px-2 py-0.5 rounded-g-pill border",
        "text-g-micro font-medium leading-tight whitespace-nowrap",
        hideOnMobile && "hidden sm:inline-flex",
        className,
      )}
      style={TONE_STYLE[tone]}
    >
      {dot ? (
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: "currentColor", opacity: 0.85 }}
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  );
}
